"use client";
import { Toaster as Sonner, toast } from "sonner";
import rehypeRaw from "rehype-raw";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import { HeaderTwo } from "../Elements/HeaderTwo/HeaderTwo";
import { useEffect, useState, useCallback, createRef, useRef } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ReCAPTCHA from "react-google-recaptcha";
import Link from "next/link";

import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";

const FIELD_CONFIG_KEYS = {
  "Text Field": "text_field",
  "Email Address": "email_address",
  "Phone Number": "phone_number",
  "Text Area": "text_area",
  "Select Box": "select_box",
  "Radio Button": "radio_button",
  "Check Box": "check_box",
  "Countries List": "countries_list",
};

function getFieldConfig(field) {
  const key = FIELD_CONFIG_KEYS[field.choose_a_field];
  return key ? field[key] : null;
}

function isConditionalActive(config) {
  if (!config) return false;
  return (
    config.conditional === "Yes" ||
    config.conditional === true ||
    config.conditional === 1
  );
}

function getConditionalSettings(config) {
  if (!isConditionalActive(config)) return null;

  const fieldName =
    config.condition_field_name ??
    config.conditional_field_name ??
    config.fieldname ??
    config.conditional_fieldname;
  const value =
    config.condition_value ??
    config.conditional_value ??
    config.value ??
    config.conditional_field_value;

  if (!fieldName) return null;

  return { fieldName, value: value ?? "" };
}

function matchesConditionalValue(currentValue, expectedValue) {
  if (currentValue == null || currentValue === "") return false;

  const expected = String(expectedValue);

  if (Array.isArray(currentValue)) {
    return currentValue.includes(expected);
  }

  const current = String(currentValue);

  if (current.includes(", ")) {
    return current.split(", ").includes(expected);
  }

  return current === expected;
}

function isFieldVisible(config, fieldValues) {
  const conditional = getConditionalSettings(config);
  if (!conditional) return true;

  const currentValue = fieldValues[conditional.fieldName] ?? "";
  return matchesConditionalValue(currentValue, conditional.value);
}

function getFieldMeta(field) {
  switch (field.choose_a_field) {
    case "Text Field":
      return {
        FieldName: field.text_field.field_name,
        FieldPlaceholder: field.text_field.placeholder,
        FieldType: "TextField",
        config: field.text_field,
      };
    case "Email Address":
      return {
        FieldName: field.email_address.field_name,
        FieldPlaceholder: field.email_address.placeholder,
        FieldType: "EmailAddress",
        config: field.email_address,
      };
    case "Phone Number":
      return {
        FieldName: field.phone_number.field_name,
        FieldPlaceholder: field.phone_number.placeholder,
        FieldType: "PhoneNumber",
        config: field.phone_number,
      };
    case "Text Area":
      return {
        FieldName: field.text_area.field_name,
        FieldPlaceholder: field.text_area.placeholder,
        FieldType: "TextArea",
        config: field.text_area,
      };
    case "Select Box":
      return {
        FieldName: field.select_box.field_name,
        FieldPlaceholder: field.select_box.placeholder,
        FieldType: "SelectBox",
        config: field.select_box,
      };
    case "Radio Button":
      return {
        FieldName: field.radio_button.field_name,
        FieldPlaceholder: field.radio_button.placeholder,
        FieldType: "RadioButton",
        GroupName: field.radio_button.field_name,
        config: field.radio_button,
      };
    case "Check Box":
      return {
        FieldName: field.check_box.field_name,
        FieldPlaceholder: field.check_box.placeholder,
        FieldType: "CheckBox",
        GroupName: field.check_box.field_name,
        config: field.check_box,
      };
    case "Countries List":
      return {
        FieldName: field.countries_list.field_name,
        FieldPlaceholder: field.countries_list.placeholder,
        FieldType: "CountriesList",
        GroupName: field.countries_list.field_name,
        config: field.countries_list,
      };
    default:
      return null;
  }
}

export const FormBuilder = ({ data, id }) => {
  // console.log(data)

  // useEffect(() => {
  //   toast.success("Form submitted!", { duration: 34343443 });
  // }, []);
  //RECAPTCHA CODEe
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [currentUrl, setCurrentUrl] = useState("");
  const [fieldValues, setFieldValues] = useState({});

  const path = usePathname();

  const [inputWidth, setInputWidth] = useState("45%");

  // console.log(data)
  const [captchaPassed, setCaptchaPassed] = useState(false);

  useEffect(() => {
    const recaptchaCheckbox = document.querySelector(".recaptcha-checkbox");
  });

  // Update the 'currentUrl' state when the route changes
  useEffect(() => {
    setCurrentUrl(path);
  }, [path]);

  const handleFieldChange = (fieldName, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleCheckboxGroupChange = (fieldName, optionValue, checked) => {
    setFieldValues((prev) => {
      const currentValues = prev[fieldName]
        ? prev[fieldName].split(", ").filter(Boolean)
        : [];
      const nextValues = checked
        ? [...new Set([...currentValues, optionValue])]
        : currentValues.filter((value) => value !== optionValue);

      return { ...prev, [fieldName]: nextValues.join(", ") };
    });
  };

  const [send, setSend] = useState(false);
  const recaptchaRef = useRef(null);

  async function handleSubmit(e) {
    e.preventDefault();

    const token = recaptchaRef.current.getValue();

    if (token) {
      const listOfUpdatedFields = [];

      //add email recipients to array
      listOfUpdatedFields.push({
        emailList: data.notifications_emails_to,
      });

      //add email subject to array
      listOfUpdatedFields.push({
        emailSubject: data.email_subject,
      });

      //add current url to array
      listOfUpdatedFields.push({
        currentUrl: currentUrl,
      });

      let clientEmailField = "";

      try {
        if (data.client_notification_field) {
          clientEmailField =
            document.getElementById(data.client_notification_field).value ?? "";
        }
      } catch (e) { }

      //add client email to array
      listOfUpdatedFields.push({
        clientEmail: clientEmailField,
      });

      for (const field of data.fields) {
        const fieldMeta = getFieldMeta(field);
        if (!fieldMeta || !isFieldVisible(fieldMeta.config, fieldValues)) {
          continue;
        }

        const id = fieldMeta.FieldName;
        const placeholder = fieldMeta.FieldPlaceholder;
        const value = fieldValues[id] ?? "";

        listOfUpdatedFields.push({ id, placeholder, value });
      }

      setFieldValues({});

      //listOfUpdatedFields = the fieldName, fieldPlaceHolder and fieldValue so we can use it to process the forms.
      // console.log(listOfUpdatedFields);

      //PROCESS FORM HERE

      const JSONdata = JSON.stringify(listOfUpdatedFields);

      // console.log(JSONdata)

      const endpoint = "/api/email";

      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSONdata,
      };

      async function submitData(endpoint, options) {
        const response = await fetch(endpoint, options);

        console.log("okay!");
        if (response.ok) {
          toast.success("Form submitted!");
        } else {
          toast.error("Form not submitted...");
        }
      }

      setSend(true);
      submitData(endpoint, options);
      setSend(false);
      recaptchaRef.current.reset();
    } else {
      toast.error("Form not submitted...");
    }
  }

  const [isRequired, setIsRequired] = useState(false);

  return (
    <>
      <p id="captchaText" className="hidden"></p>
      <Sonner
        className="toaster group"
        toastOptions={{
          classNames: {
            toast:
              "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
            description: "group-[.toast]:text-muted-foreground",
            actionButton:
              "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
            cancelButton:
              "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          },
        }}
      />

      <section id={id} className="FormBuilder  relative my-[100px]">
        <HeaderTwo classes="text-center !w-[100%] text-3xl lg:!text-5xl mb-[50px] px-[25px] ">
          {data.form_title}
        </HeaderTwo>

        {data.form_body ? (
          <p className="leading-[2] w-[85%] mx-auto lg:w-[50%] my-[50px] text-center ">
            {data.form_body}
          </p>
        ) : null}

        <form
          onSubmit={handleSubmit}
          className="formBuilderForm w-[90%] md:w-[700px] mx-auto flex flex-col items-end gap-[20px]  "
        >
          <div className="w-[100%] flex flex-row flex-wrap justify-between gap-y-[20px] ">
            {(Array.isArray(data?.fields) ? data.fields : []).map((field) => {
              switch (field.choose_a_field) {
                // T E X T  F I E L D
                case "Text Field": {
                  const config = field.text_field;
                  if (!isFieldVisible(config, fieldValues)) return null;

                  return (
                    <div
                      key={config.field_name}
                      className={
                        config.size === "100%"
                          ? "w-[100%] flex flex-col gap-y-[20px]"
                          : "w-[47%] flex flex-col gap-y-[20px]"
                      }
                    >
                      {config.header ? <h5>{config.header}</h5> : null}
                      {config.body ? (
                        <ReactMarkdown
                          className="formItem"
                          rehypePlugins={[rehypeRaw]}
                        >
                          {config.body.replace(/\n/gi, "<br/> \n")}
                        </ReactMarkdown>
                      ) : null}

                      <input
                        aria-label={config?.placeholder ?? "Text Field"}
                        required={config.required === "Yes" ? true : null}
                        placeholder={
                          config.required === "Yes"
                            ? config.placeholder + " *"
                            : config.placeholder
                        }
                        className={
                          config.size === "100%"
                            ? "w-[100%] box-border !border-[#949494] border-[0.5px] lg:mr-[20px]"
                            : "w-[100%] box-border !border-[#949494] border-[0.5px] lg:mr-[20px]"
                        }
                        type="text"
                        id={config.field_name}
                        name={config.field_name}
                        value={fieldValues[config.field_name] ?? ""}
                        onChange={(e) =>
                          handleFieldChange(config.field_name, e.target.value)
                        }
                      />
                    </div>
                  );
                }

                // E M A I L  A D D R E S S
                case "Email Address": {
                  const config = field.email_address;
                  if (!isFieldVisible(config, fieldValues)) return null;

                  return (
                    <div
                      key={config.field_name}
                      className={
                        config.size === "100%"
                          ? "w-[100%]  flex flex-col gap-y-[20px]"
                          : "w-[47%] flex flex-col gap-y-[20px]"
                      }
                    >
                      {config.header ? <h5>{config.header}</h5> : null}
                      {config.body ? (
                        <ReactMarkdown
                          className="formItem"
                          rehypePlugins={[rehypeRaw]}
                        >
                          {config.body.replace(/\n/gi, "<br/> \n")}
                        </ReactMarkdown>
                      ) : null}

                      <input
                        aria-label={config?.placeholder ?? "Email Field"}
                        required={config.required === "Yes" ? true : null}
                        placeholder={
                          config.required === "Yes"
                            ? config.placeholder + " *"
                            : config.placeholder
                        }
                        className={
                          config.size === "100%"
                            ? "w-[100%] box-border !border-[#949494] border-[0.5px] lg:mr-[20px]"
                            : "w-[100%] box-border !border-[#949494] border-[0.5px] lg:mr-[20px]"
                        }
                        type="email"
                        id={config.field_name}
                        name={config.field_name}
                        value={fieldValues[config.field_name] ?? ""}
                        onChange={(e) =>
                          handleFieldChange(config.field_name, e.target.value)
                        }
                      />
                    </div>
                  );
                }

                // P H O N E  N U M B E R
                case "Phone Number": {
                  const config = field.phone_number;
                  if (!isFieldVisible(config, fieldValues)) return null;

                  return (
                    <div
                      key={config.field_name}
                      className={
                        config.size === "100%"
                          ? "w-[100%]  flex flex-col gap-y-[20px]"
                          : "w-[47%] flex flex-col gap-y-[20px]"
                      }
                    >
                      {config.header ? <h5>{config.header}</h5> : null}
                      {config.body ? (
                        <ReactMarkdown
                          className="formItem"
                          rehypePlugins={[rehypeRaw]}
                        >
                          {config.body.replace(/\n/gi, "<br/> \n")}
                        </ReactMarkdown>
                      ) : null}

                      <input
                        aria-label={config?.placeholder ?? "Phone Field"}
                        required={config.required === "Yes" ? true : null}
                        placeholder={
                          config.required === "Yes"
                            ? config.placeholder + " *"
                            : config.placeholder
                        }
                        className={
                          config.size === "100%"
                            ? "w-[100%] box-border !border-[#949494] border-[0.5px] lg:mr-[20px]"
                            : "w-[100%] box-border !border-[#949494] border-[0.5px] lg:mr-[20px]"
                        }
                        type="tel"
                        id={config.field_name}
                        name={config.field_name}
                        value={fieldValues[config.field_name] ?? ""}
                        onChange={(e) =>
                          handleFieldChange(config.field_name, e.target.value)
                        }
                      />
                    </div>
                  );
                }

                // T E X T  A R E A
                case "Text Area": {
                  const config = field.text_area;
                  if (!isFieldVisible(config, fieldValues)) return null;

                  return (
                    <div key={config.field_name}>
                      {config.header ? <h5>{config.header}</h5> : null}
                      {config.body ? (
                        <ReactMarkdown
                          className="formItem"
                          rehypePlugins={[rehypeRaw]}
                        >
                          {config.body.replace(/\n/gi, "<br/> \n")}
                        </ReactMarkdown>
                      ) : null}

                      <textarea
                        aria-label={config?.placeholder ?? "Text Area Field"}
                        required={config.required === "Yes" ? true : null}
                        placeholder={
                          config.required === "Yes"
                            ? config.placeholder + " *"
                            : config.placeholder
                        }
                        className={
                          "w-[100%] box-border !border-[#949494] border-[0.5px] "
                        }
                        id={config.field_name}
                        name={config.field_name}
                        value={fieldValues[config.field_name] ?? ""}
                        onChange={(e) =>
                          handleFieldChange(config.field_name, e.target.value)
                        }
                      ></textarea>
                    </div>
                  );
                }

                // L I N E  B R E A K

                case "Line Break":
                  // console.log(field.line_break);
                  return (
                    <div
                      key={field.line_break}
                      style={{ backgroundColor: `${field.line_break}` }}
                      className="h-[2px] w-[100%] my-[15px] "
                    ></div>
                  );
                  break;

                // S E L E C T  B O X
                case "Select Box": {
                  const config = field.select_box;
                  if (!isFieldVisible(config, fieldValues)) return null;

                  return (
                    <div key={config.field_name}>
                      {config.header ? <h5>{config.header}</h5> : null}
                      {config.body ? (
                        <ReactMarkdown
                          className="formItem"
                          rehypePlugins={[rehypeRaw]}
                        >
                          {config.body.replace(/\n/gi, "<br/> \n")}
                        </ReactMarkdown>
                      ) : null}

                      <select
                        aria-label={config?.placeholder ?? "Select Box Field"}
                        required={config.required === "Yes" ? true : null}
                        className={
                          "w-[100%] box-border !border-[#949494] border-[0.5px]"
                        }
                        id={config.field_name}
                        name={config.field_name}
                        value={fieldValues[config.field_name] ?? ""}
                        onChange={(e) =>
                          handleFieldChange(config.field_name, e.target.value)
                        }
                      >
                        <option value="" disabled>
                          {config.placeholder}
                          {config.required == "Yes" ? " *" : null}
                        </option>
                        {config.option.map((option, index) => (
                          <option key={index} value={option.option_placeholder}>
                            {option.option_placeholder}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }

                // R A D I O  B U T T O N
                case "Radio Button": {
                  const config = field.radio_button;
                  if (!isFieldVisible(config, fieldValues)) return null;

                  return (
                    <div key={config.field_name}>
                      <div className="w-[100%] flex flex-row gap-x-[25px] flex-wrap">
                        <p className="font-semibold w-[100%] ">
                          {config.placeholder}
                          {config.required === "Yes" ? " *" : null}
                        </p>

                        {config.radio.map((option, index) => (
                          <div
                            className="flex flex-row justify-center items-center gap-[20px]"
                            key={index}
                          >
                            <input
                              aria-label={
                                config?.placeholder ?? "Radio Button Field"
                              }
                              required={
                                config.required === "Yes" ? true : null
                              }
                              type="radio"
                              id={`${config.field_name}-${option.radio_label}`}
                              name={config.field_name}
                              value={option.radio_label}
                              checked={
                                fieldValues[config.field_name] ===
                                option.radio_label
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  config.field_name,
                                  e.target.value,
                                )
                              }
                            />
                            <label
                              htmlFor={`${config.field_name}-${option.radio_label}`}
                            >
                              {option.radio_label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // C H E C K B O X
                case "Check Box": {
                  const config = field.check_box;
                  if (!isFieldVisible(config, fieldValues)) return null;

                  const selectedValues = fieldValues[config.field_name]
                    ? fieldValues[config.field_name].split(", ").filter(Boolean)
                    : [];

                  return (
                    <div key={config.field_name}>
                      <div className="w-[100%] flex flex-row gap-x-[25px] flex-wrap">
                        <p className="font-semibold w-[100%] ">
                          {config.placeholder}{" "}
                        </p>
                        {config.checkboxes.map((option, index) => (
                          <div
                            className="flex flex-row justify-center items-center gap-[20px]"
                            key={index}
                          >
                            <input
                              aria-label={
                                config?.placeholder ?? "Check Box Field"
                              }
                              type="checkbox"
                              id={`${config.field_name}-${option.checkbox_label}`}
                              name={config.field_name}
                              value={option.checkbox_label}
                              checked={selectedValues.includes(
                                option.checkbox_label,
                              )}
                              onChange={(e) =>
                                handleCheckboxGroupChange(
                                  config.field_name,
                                  option.checkbox_label,
                                  e.target.checked,
                                )
                              }
                            />
                            <label
                              htmlFor={`${config.field_name}-${option.checkbox_label}`}
                            >
                              {option.checkbox_label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // C O U N T R I E S  D R O P  D O W N
                case "Countries List": {
                  const config = field.countries_list;
                  if (!isFieldVisible(config, fieldValues)) return null;

                  return (
                    <select
                      aria-label={
                        config?.placeholder ?? "Countries List Field"
                      }
                      required={config.required === "Yes" ? true : null}
                      className={
                        "w-[100%] box-border !border-[#949494] border-[0.5px]"
                      }
                      key={config.field_name}
                      id={config.field_name}
                      name={config.field_name}
                      value={fieldValues[config.field_name] ?? ""}
                      onChange={(e) =>
                        handleFieldChange(config.field_name, e.target.value)
                      }
                    >
                        <option>Select a Country</option>
                        <option value="United Kingdom">United Kingdom</option>

                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Aland Islands">Aland Islands</option>
                        <option value="Albania">Albania</option>
                        <option value="Algeria">Algeria</option>
                        <option value="American Samoa">American Samoa</option>
                        <option value="Andorra">Andorra</option>
                        <option value="Angola">Angola</option>
                        <option value="Anguilla">Anguilla</option>
                        <option value="Antarctica">Antarctica</option>
                        <option value="Antigua and Barbuda">
                          Antigua and Barbuda
                        </option>
                        <option value="Argentina">Argentina</option>
                        <option value="Armenia">Armenia</option>
                        <option value="Aruba">Aruba</option>
                        <option value="Australia">Australia</option>
                        <option value="Austria">Austria</option>
                        <option value="Azerbaijan">Azerbaijan</option>
                        <option value="Bahamas">Bahamas</option>
                        <option value="Bahrain">Bahrain</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="Barbados">Barbados</option>
                        <option value="Belarus">Belarus</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Belize">Belize</option>
                        <option value="Benin">Benin</option>
                        <option value="Bermuda">Bermuda</option>
                        <option value="Bhutan">Bhutan</option>
                        <option value="Bolivia">Bolivia</option>
                        <option value="Bonaire, Sint Eustatius and Saba">
                          Bonaire, Sint Eustatius and Saba
                        </option>
                        <option value="Bosnia and Herzegovina">
                          Bosnia and Herzegovina
                        </option>
                        <option value="Botswana">Botswana</option>
                        <option value="Bouvet Island">Bouvet Island</option>
                        <option value="Brazil">Brazil</option>
                        <option value="British Indian Ocean Territory">
                          British Indian Ocean Territory
                        </option>
                        <option value="Brunei Darussalam">
                          Brunei Darussalam
                        </option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Burkina Faso">Burkina Faso</option>
                        <option value="Burundi">Burundi</option>
                        <option value="Cambodia">Cambodia</option>
                        <option value="Cameroon">Cameroon</option>
                        <option value="Canada">Canada</option>
                        <option value="Cape Verde">Cape Verde</option>
                        <option value="Cayman Islands">Cayman Islands</option>
                        <option value="Central African Republic">
                          Central African Republic
                        </option>
                        <option value="Chad">Chad</option>
                        <option value="Chile">Chile</option>
                        <option value="China">China</option>
                        <option value="Christmas Island">
                          Christmas Island
                        </option>
                        <option value="Cocos (Keeling) Islands">
                          Cocos (Keeling) Islands
                        </option>
                        <option value="Colombia">Colombia</option>
                        <option value="Comoros">Comoros</option>
                        <option value="Congo">Congo</option>
                        <option value="Congo, Democratic Republic of the Congo">
                          Congo, Democratic Republic of the Congo
                        </option>
                        <option value="Cook Islands">Cook Islands</option>
                        <option value="Costa Rica">Costa Rica</option>
                        <option value="Cote DIvoire">Cote DIvoire</option>
                        <option value="Croatia">Croatia</option>
                        <option value="Cuba">Cuba</option>
                        <option value="Curacao">Curacao</option>
                        <option value="Cyprus">Cyprus</option>
                        <option value="Czech Republic">Czech Republic</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Djibouti">Djibouti</option>
                        <option value="Dominica">Dominica</option>
                        <option value="Dominican Republic">
                          Dominican Republic
                        </option>
                        <option value="Ecuador">Ecuador</option>
                        <option value="Egypt">Egypt</option>
                        <option value="El Salvador">El Salvador</option>
                        <option value="Equatorial Guinea">
                          Equatorial Guinea
                        </option>
                        <option value="Eritrea">Eritrea</option>
                        <option value="Estonia">Estonia</option>
                        <option value="Ethiopia">Ethiopia</option>
                        <option value="Falkland Islands (Malvinas)">
                          Falkland Islands (Malvinas)
                        </option>
                        <option value="Faroe Islands">Faroe Islands</option>
                        <option value="Fiji">Fiji</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="French Guiana">French Guiana</option>
                        <option value="French Polynesia">
                          French Polynesia
                        </option>
                        <option value="French Southern Territories">
                          French Southern Territories
                        </option>
                        <option value="Gabon">Gabon</option>
                        <option value="Gambia">Gambia</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Germany">Germany</option>
                        <option value="Ghana">Ghana</option>
                        <option value="Gibraltar">Gibraltar</option>
                        <option value="Greece">Greece</option>
                        <option value="Greenland">Greenland</option>
                        <option value="Grenada">Grenada</option>
                        <option value="Guadeloupe">Guadeloupe</option>
                        <option value="Guam">Guam</option>
                        <option value="Guatemala">Guatemala</option>
                        <option value="Guernsey">Guernsey</option>
                        <option value="Guinea">Guinea</option>
                        <option value="Guinea-Bissau">Guinea-Bissau</option>
                        <option value="Guyana">Guyana</option>
                        <option value="Haiti">Haiti</option>
                        <option value="Heard Island and Mcdonald Islands">
                          Heard Island and Mcdonald Islands
                        </option>
                        <option value="Holy See (Vatican City State)">
                          Holy See (Vatican City State)
                        </option>
                        <option value="Honduras">Honduras</option>
                        <option value="Hong Kong">Hong Kong</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Iceland">Iceland</option>
                        <option value="India">India</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Iran, Islamic Republic of">
                          Iran, Islamic Republic of
                        </option>
                        <option value="Iraq">Iraq</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Isle of Man">Isle of Man</option>
                        <option value="Israel">Israel</option>
                        <option value="Italy">Italy</option>
                        <option value="Jamaica">Jamaica</option>
                        <option value="Japan">Japan</option>
                        <option value="Jersey">Jersey</option>
                        <option value="Jordan">Jordan</option>
                        <option value="Kazakhstan">Kazakhstan</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Kiribati">Kiribati</option>
                        <option value="Korea, Democratic Peoples Republic of">
                          Korea, Democratic Peoples Republic of
                        </option>
                        <option value="Korea, Republic of">
                          Korea, Republic of
                        </option>
                        <option value="Kosovo">Kosovo</option>
                        <option value="Kuwait">Kuwait</option>
                        <option value="Kyrgyzstan">Kyrgyzstan</option>
                        <option value="Lao Peoples Democratic Republic">
                          Lao Peoples Democratic Republic
                        </option>
                        <option value="Latvia">Latvia</option>
                        <option value="Lebanon">Lebanon</option>
                        <option value="Lesotho">Lesotho</option>
                        <option value="Liberia">Liberia</option>
                        <option value="Libyan Arab Jamahiriya">
                          Libyan Arab Jamahiriya
                        </option>
                        <option value="Liechtenstein">Liechtenstein</option>
                        <option value="Lithuania">Lithuania</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Macao">Macao</option>
                        <option value="Macedonia, the Former Yugoslav Republic of">
                          Macedonia, the Former Yugoslav Republic of
                        </option>
                        <option value="Madagascar">Madagascar</option>
                        <option value="Malawi">Malawi</option>
                        <option value="Malaysia">Malaysia</option>
                        <option value="Maldives">Maldives</option>
                        <option value="Mali">Mali</option>
                        <option value="Malta">Malta</option>
                        <option value="Marshall Islands">
                          Marshall Islands
                        </option>
                        <option value="Martinique">Martinique</option>
                        <option value="Mauritania">Mauritania</option>
                        <option value="Mauritius">Mauritius</option>
                        <option value="Mayotte">Mayotte</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Micronesia, Federated States of">
                          Micronesia, Federated States of
                        </option>
                        <option value="Moldova, Republic of">
                          Moldova, Republic of
                        </option>
                        <option value="Monaco">Monaco</option>
                        <option value="Mongolia">Mongolia</option>
                        <option value="Montenegro">Montenegro</option>
                        <option value="Montserrat">Montserrat</option>
                        <option value="Morocco">Morocco</option>
                        <option value="Mozambique">Mozambique</option>
                        <option value="Myanmar">Myanmar</option>
                        <option value="Namibia">Namibia</option>
                        <option value="Nauru">Nauru</option>
                        <option value="Nepal">Nepal</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="Netherlands Antilles">
                          Netherlands Antilles
                        </option>
                        <option value="New Caledonia">New Caledonia</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Nicaragua">Nicaragua</option>
                        <option value="Niger">Niger</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="Niue">Niue</option>
                        <option value="Norfolk Island">Norfolk Island</option>
                        <option value="Northern Mariana Islands">
                          Northern Mariana Islands
                        </option>
                        <option value="Norway">Norway</option>
                        <option value="Oman">Oman</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Palau">Palau</option>
                        <option value="Palestinian Territory, Occupied">
                          Palestinian Territory, Occupied
                        </option>
                        <option value="Panama">Panama</option>
                        <option value="Papua New Guinea">
                          Papua New Guinea
                        </option>
                        <option value="Paraguay">Paraguay</option>
                        <option value="Peru">Peru</option>
                        <option value="Philippines">Philippines</option>
                        <option value="Pitcairn">Pitcairn</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Puerto Rico">Puerto Rico</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Reunion">Reunion</option>
                        <option value="Romania">Romania</option>
                        <option value="Russian Federation">
                          Russian Federation
                        </option>
                        <option value="Rwanda">Rwanda</option>
                        <option value="Saint Barthelemy">
                          Saint Barthelemy
                        </option>
                        <option value="Saint Helena">Saint Helena</option>
                        <option value="Saint Kitts and Nevis">
                          Saint Kitts and Nevis
                        </option>
                        <option value="Saint Lucia">Saint Lucia</option>
                        <option value="Saint Martin">Saint Martin</option>
                        <option value="Saint Pierre and Miquelon">
                          Saint Pierre and Miquelon
                        </option>
                        <option value="Saint Vincent and the Grenadines">
                          Saint Vincent and the Grenadines
                        </option>
                        <option value="Samoa">Samoa</option>
                        <option value="San Marino">San Marino</option>
                        <option value="Sao Tome and Principe">
                          Sao Tome and Principe
                        </option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Senegal">Senegal</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Serbia and Montenegro">
                          Serbia and Montenegro
                        </option>
                        <option value="Seychelles">Seychelles</option>
                        <option value="Sierra Leone">Sierra Leone</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Sint Maarten">Sint Maarten</option>
                        <option value="Slovakia">Slovakia</option>
                        <option value="Slovenia">Slovenia</option>
                        <option value="Solomon Islands">Solomon Islands</option>
                        <option value="Somalia">Somalia</option>
                        <option value="South Africa">South Africa</option>
                        <option value="South Georgia and the South Sandwich Islands">
                          South Georgia and the South Sandwich Islands
                        </option>
                        <option value="South Sudan">South Sudan</option>
                        <option value="Spain">Spain</option>
                        <option value="Sri Lanka">Sri Lanka</option>
                        <option value="Sudan">Sudan</option>
                        <option value="Suriname">Suriname</option>
                        <option value="Svalbard and Jan Mayen">
                          Svalbard and Jan Mayen
                        </option>
                        <option value="Swaziland">Swaziland</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Syrian Arab Republic">
                          Syrian Arab Republic
                        </option>
                        <option value="Taiwan, Province of China">
                          Taiwan, Province of China
                        </option>
                        <option value="Tajikistan">Tajikistan</option>
                        <option value="Tanzania, United Republic of">
                          Tanzania, United Republic of
                        </option>
                        <option value="Thailand">Thailand</option>
                        <option value="Timor-Leste">Timor-Leste</option>
                        <option value="Togo">Togo</option>
                        <option value="Tokelau">Tokelau</option>
                        <option value="Tonga">Tonga</option>
                        <option value="Trinidad and Tobago">
                          Trinidad and Tobago
                        </option>
                        <option value="Tunisia">Tunisia</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Turkmenistan">Turkmenistan</option>
                        <option value="Turks and Caicos Islands">
                          Turks and Caicos Islands
                        </option>
                        <option value="Tuvalu">Tuvalu</option>
                        <option value="Uganda">Uganda</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="United Arab Emirates">
                          United Arab Emirates
                        </option>
                        <option value="United States">United States</option>
                        <option value="United States Minor Outlying Islands">
                          United States Minor Outlying Islands
                        </option>
                        <option value="Uruguay">Uruguay</option>
                        <option value="Uzbekistan">Uzbekistan</option>
                        <option value="Vanuatu">Vanuatu</option>
                        <option value="Venezuela">Venezuela</option>
                        <option value="Viet Nam">Viet Nam</option>
                        <option value="Virgin Islands, British">
                          Virgin Islands, British
                        </option>
                        <option value="Virgin Islands, U.s.">
                          Virgin Islands, U.s.
                        </option>
                        <option value="Wallis and Futuna">
                          Wallis and Futuna
                        </option>
                        <option value="Western Sahara">Western Sahara</option>
                        <option value="Yemen">Yemen</option>
                        <option value="Zambia">Zambia</option>
                        <option value="Zimbabwe">Zimbabwe</option>
                      </select>
                  );
                }
              }
            })}
          </div>

          <div className="flex flex-row w-[100%] items-center justify-end">
            <p className="mr-[10px]">
              I have read and agree to the{" "}
              <Link className="text-[#1875A0] font-bold" href="/privacy-policy">
                privacy policy
              </Link>
              .
            </p>
            <input
              aria-label="Consent Checkbox"
              type="checkbox"
              id="consent"
              name="consent"
              required
              value="privacyConsent"
            />
          </div>

          <ReCAPTCHA
            sitekey="6LcehJ8kAAAAAIgVCtwTEexY6fbTscvKQ3FmmTNN"
            ref={recaptchaRef}
          />
          <input
            className="cursor-pointer lg:mr-[20px] w-[150px] mt-[25px] !z-[15]"
            type="submit"
            value={data.button_value}
          />
        </form>
      </section>
    </>
  );
};
